/**
 * ComparisonView Component
 *
 * Unified comparison view for side-by-side record comparison.
 * Supports two modes:
 * - 'directed': Old -> New flow (lifecycle activation). Arrow separator, right column highlighted.
 * - 'arbitrary': Any two records. "vs" separator, both columns can highlight.
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { Typography, Switch, Space, Button, Tooltip, Empty, message } from 'antd'
import { SwapOutlined, CopyOutlined, CheckOutlined, DiffOutlined, PushpinOutlined } from '@ant-design/icons'
import type { ComparisonViewProps, ComparisonLabels, ComparisonMode, TemporalContext } from './types'
import { useComparisonDiff } from './useComparisonDiff'
import { ComparisonSection } from './ComparisonSection'
import { ComparisonRow } from './ComparisonRow'
import { formatComparisonAsText, copyTextToClipboard } from './utils/clipboard'
import './ComparisonView.css'

const { Text } = Typography

/**
 * Get default labels based on mode.
 */
function getDefaultLabels(mode: ComparisonMode): ComparisonLabels {
  if (mode === 'directed') {
    return { left: 'Current', right: 'Revised' }
  }
  return { left: 'Record A', right: 'Record B' }
}

/**
 * Parse effectiveFrom field from a record.
 * Returns Date if valid, null otherwise.
 */
function parseEffectiveFrom(
  record: Record<string, unknown> | null,
  fieldName: string
): Date | null {
  if (!record) return null
  const value = record[fieldName]
  if (value === null || value === undefined) return null
  if (typeof value === 'string') {
    const date = new Date(value)
    return isNaN(date.getTime()) ? null : date
  }
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value
  }
  return null
}

/**
 * Format a date for display in column labels.
 * Returns short format like "Jul 1" or "Immediate" for null.
 */
function formatDateForLabel(date: Date | null): string {
  if (!date) return 'Immediate'
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${months[date.getMonth()]} ${date.getDate()}`
}

/**
 * Compute temporal context for two records.
 * Determines which record is earlier and whether they need to be swapped.
 *
 * @param leftRecord - Left input record
 * @param rightRecord - Right input record
 * @param effectiveFromField - Field name to read date from (default: 'effectiveFrom')
 * @param inputAnchor - Which input record is the anchor ('left' or 'right')
 * @returns TemporalContext or null if temporal ordering cannot be determined
 */
function computeTemporalContext(
  leftRecord: Record<string, unknown> | null,
  rightRecord: Record<string, unknown> | null,
  effectiveFromField: string,
  inputAnchor: 'left' | 'right'
): TemporalContext | null {
  // Need both records to compute temporal context
  if (!leftRecord || !rightRecord) return null

  const leftDate = parseEffectiveFrom(leftRecord, effectiveFromField)
  const rightDate = parseEffectiveFrom(rightRecord, effectiveFromField)

  // If both dates are null (immediate), no swap needed
  // If dates are the same, no swap needed
  const leftTime = leftDate?.getTime() ?? 0 // null = immediate = earliest
  const rightTime = rightDate?.getTime() ?? 0

  // Determine if swap is needed: left should be earlier (smaller time)
  const needsSwap = leftTime > rightTime

  // After potential swap, compute labels
  const earlierDate = needsSwap ? rightDate : leftDate
  const laterDate = needsSwap ? leftDate : rightDate

  const leftDateLabel = formatDateForLabel(earlierDate)
  const rightDateLabel = formatDateForLabel(laterDate)

  // Anchor position after swap
  // If inputAnchor was 'right' and we swapped, anchor is now 'left'
  // If inputAnchor was 'left' and we swapped, anchor is now 'right'
  const anchorPosition: 'left' | 'right' = needsSwap
    ? (inputAnchor === 'right' ? 'left' : 'right')
    : inputAnchor

  return {
    wasSwapped: needsSwap,
    leftDateLabel,
    rightDateLabel,
    leftColumnLabel: `Earlier (${leftDateLabel})`,
    rightColumnLabel: `Later (${rightDateLabel})`,
    anchorPosition,
  }
}

/**
 * Unified comparison view component.
 *
 * @example Directed mode (SplicingPreviewModal)
 * ```tsx
 * <ComparisonView
 *   leftRecord={selectedRecord?.record ?? null}
 *   rightRecord={newRecord}
 *   mode="directed"
 *   labels={{ left: 'Current', right: 'Revised' }}
 *   fields={fieldMetadata}
 *   comparison={{ keyFields }}
 *   maxHeight={300}
 * />
 * ```
 *
 * @example Arbitrary mode (ComparisonModal)
 * ```tsx
 * <ComparisonView
 *   leftRecord={comparisonRecord}
 *   rightRecord={anchorRecord}
 *   mode="arbitrary"
 *   labels={{
 *     left: comparisonRecord?.name || 'Record A',
 *     right: anchorRecord?.name || 'Record B'
 *   }}
 *   showSwapButton
 *   onSwap={handleSwap}
 *   fields={metadata.fields}
 *   formSections={metadata.formSections}
 *   showDifferencesOnly={showDifferencesOnly}
 *   onToggleShowDifferencesOnly={toggleDifferencesOnly}
 *   showCopyButton
 *   maxHeight="60vh"
 * />
 * ```
 */
export function ComparisonView({
  leftRecord,
  rightRecord,
  mode = 'arbitrary',
  labels: customLabels,
  anchorRecord: inputAnchor = 'right',
  effectiveFromField = 'effectiveFrom',
  leftColumnColor,
  rightColumnColor,
  showSwapButton = false,
  onSwap,
  fields,
  formSections = [],
  comparison,
  showDifferencesOnly: showDifferencesOnlyProp,
  onToggleShowDifferencesOnly,
  showCopyButton = false,
  maxHeight,
  className = '',
  collapsible = true,
}: ComparisonViewProps): JSX.Element {
  // Compute temporal context for date-aware ordering
  const temporalContext = useMemo<TemporalContext | null>(() => {
    return computeTemporalContext(leftRecord, rightRecord, effectiveFromField, inputAnchor)
  }, [leftRecord, rightRecord, effectiveFromField, inputAnchor])

  // Determine effective record positions:
  // - When no comparison: new/anchor record on LEFT, empty comparison on RIGHT (so user can see record details)
  // - When comparison selected: use temporal ordering (earlier on left, later on right)
  const [effectiveLeftRecord, effectiveRightRecord] = useMemo(() => {
    // No comparison selected - put the anchor/new record on left, empty on right
    if (!leftRecord && rightRecord) {
      return [rightRecord, null]
    }
    // Both records present - use temporal ordering
    if (temporalContext?.wasSwapped) {
      return [rightRecord, leftRecord]
    }
    return [leftRecord, rightRecord]
  }, [leftRecord, rightRecord, temporalContext?.wasSwapped])

  // Swap colors to match record positions
  const [effectiveLeftColor, effectiveRightColor] = useMemo(() => {
    // No comparison selected - anchor color goes to left
    if (!leftRecord && rightRecord) {
      return [rightColumnColor, leftColumnColor]
    }
    // Both records present - use temporal ordering
    if (temporalContext?.wasSwapped) {
      return [rightColumnColor, leftColumnColor]
    }
    return [leftColumnColor, rightColumnColor]
  }, [leftRecord, rightRecord, leftColumnColor, rightColumnColor, temporalContext?.wasSwapped])

  // Merge custom labels with defaults, using temporal labels if available and no custom labels
  const labels = useMemo<ComparisonLabels>(() => {
    // If caller provided BOTH custom labels, respect them completely
    if (customLabels?.left && customLabels?.right) {
      return customLabels as ComparisonLabels
    }

    // Use temporal labels if available (only when both records exist)
    if (temporalContext) {
      return {
        left: temporalContext.leftColumnLabel,
        right: temporalContext.rightColumnLabel,
      }
    }

    // When no comparison record selected (effectiveRightRecord is null because we flipped)
    // Left shows the new/anchor record, Right shows empty comparison
    if (effectiveLeftRecord && !effectiveRightRecord) {
      // Compute label for left record (the new/anchor record) based on its effectiveFrom date
      const leftDate = parseEffectiveFrom(effectiveLeftRecord, effectiveFromField)
      const leftDateLabel = formatDateForLabel(leftDate)
      return {
        left: leftDateLabel === 'Immediate' ? 'New Record' : `New (${leftDateLabel})`,
        right: 'Comparison',
      }
    }

    // Fall back to defaults
    return {
      ...getDefaultLabels(mode),
      ...customLabels,
    }
  }, [mode, customLabels, temporalContext, effectiveLeftRecord, effectiveRightRecord, effectiveFromField])

  // Internal show differences state (controlled or uncontrolled)
  const [internalShowDifferencesOnly, setInternalShowDifferencesOnly] = useState(
    showDifferencesOnlyProp ?? false
  )

  // Effective show differences value (prefer prop if callback provided, otherwise use internal)
  const showDifferencesOnly = onToggleShowDifferencesOnly
    ? (showDifferencesOnlyProp ?? false)
    : internalShowDifferencesOnly

  // Track collapsed sections
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set())

  // Copy state
  const [isCopied, setIsCopied] = useState(false)

  // Screen reader announcement state
  const [announcement, setAnnouncement] = useState('')
  const announcementRef = useRef<HTMLDivElement>(null)

  // Calculate differences using effective (potentially swapped) records
  const {
    sections,
    changedCount,
    unchangedCount,
    hasDifferences,
    keyFieldChanges,
  } = useComparisonDiff({
    leftRecord: effectiveLeftRecord,
    rightRecord: effectiveRightRecord,
    fields,
    formSections,
    comparison,
    showDifferencesOnly,
  })

  // Key fields set for highlighting
  const keyFields = useMemo(() => {
    return new Set(comparison?.keyFields ?? [])
  }, [comparison?.keyFields])

  // Announce comparison results to screen readers when they change
  useEffect(() => {
    if (effectiveLeftRecord && effectiveRightRecord) {
      const keyFieldInfo = keyFieldChanges.length > 0
        ? `, including ${keyFieldChanges.length} key field${keyFieldChanges.length !== 1 ? 's' : ''}`
        : ''
      const msg = hasDifferences
        ? `Comparison loaded. ${changedCount} field${changedCount !== 1 ? 's' : ''} changed${keyFieldInfo}.`
        : 'Comparison loaded. No differences found.'
      setAnnouncement(msg)
    }
  }, [effectiveLeftRecord, effectiveRightRecord, hasDifferences, changedCount, keyFieldChanges.length])

  // Handle section toggle
  const handleSectionToggle = useCallback((sectionKey: string, expanded: boolean) => {
    setCollapsedSections(prev => {
      const next = new Set(prev)
      if (expanded) {
        next.delete(sectionKey)
      } else {
        next.add(sectionKey)
      }
      return next
    })
  }, [])

  // Handle copy to clipboard
  const handleCopy = useCallback(async () => {
    try {
      const text = formatComparisonAsText(
        effectiveLeftRecord,
        effectiveRightRecord,
        sections,
        fields,
        labels
      )
      await copyTextToClipboard(text)
      setIsCopied(true)
      message.success('Comparison copied to clipboard')

      // Reset icon after 2 seconds
      setTimeout(() => setIsCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
      message.error('Failed to copy to clipboard')
    }
  }, [effectiveLeftRecord, effectiveRightRecord, sections, fields, labels])

  // Has both records?
  const hasLeftRecord = effectiveLeftRecord !== null
  const hasRightRecord = effectiveRightRecord !== null
  const hasBothRecords = hasLeftRecord && hasRightRecord

  // Total field count for summary
  const totalFieldCount = sections.reduce((sum, s) => sum + s.fields.length, 0)

  // Handle internal toggle
  const handleInternalToggle = useCallback(() => {
    setInternalShowDifferencesOnly(prev => !prev)
  }, [])

  // Determine if we should show the toolbar
  // (only when there are interactive controls to show)
  const hasToolbarControls = showSwapButton || showCopyButton || onToggleShowDifferencesOnly

  // Build CSS class list
  const cssClasses = [
    'comparison-view',
    `comparison-view--${mode}`,
    className,
  ].filter(Boolean).join(' ')

  return (
    <div
      className={cssClasses}
      role="region"
      aria-label="Record comparison"
    >
      {/* Screen reader announcements */}
      <div
        ref={announcementRef}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="comparison-view-sr-only"
      >
        {announcement}
      </div>

      {/* Header with column labels */}
      <div className="comparison-view-header">
        {/* Summary column */}
        <div className="comparison-view-header-label">
          <div className="comparison-view-header-summary">
            {!hasBothRecords ? (
              <Text type="secondary" style={{ fontSize: 12 }}>Select a record</Text>
            ) : hasDifferences ? (
              <>
                <Text style={{ fontSize: 12 }}>
                  <Text strong style={{ color: 'var(--rp-gray-700)' }}>{changedCount}</Text>
                  {' changed'}
                </Text>
                {unchangedCount > 0 && (
                  <>
                    <span className="comparison-view-header-divider">|</span>
                    <Button
                      type="link"
                      size="small"
                      style={{ padding: 0, height: 'auto', fontSize: 12 }}
                      onClick={onToggleShowDifferencesOnly || handleInternalToggle}
                    >
                      {showDifferencesOnly ? 'Show all' : 'Changes only'}
                    </Button>
                  </>
                )}
              </>
            ) : (
              <Text type="success" style={{ fontSize: 12 }}>No changes</Text>
            )}
          </div>
        </div>
        <div className={`comparison-view-header-left ${temporalContext?.anchorPosition === 'left' || !hasBothRecords ? 'is-anchor' : ''}`}>
          {/* Show color bar for left record (always has the anchor/new record when no comparison) */}
          {effectiveLeftColor && (
            <span
              className="comparison-view-color-bar"
              style={{ backgroundColor: effectiveLeftColor }}
            />
          )}
          <Text strong type="secondary" style={{ fontSize: 12, textTransform: 'uppercase' }}>
            {/* Show pin when this is anchor column (from temporal context OR when no comparison - left is always anchor) */}
            {(temporalContext?.anchorPosition === 'left' || !hasBothRecords) && (
              <PushpinOutlined className="comparison-view-anchor-pin" />
            )}
            {labels.left}
          </Text>
        </div>
        <div className="comparison-view-header-separator" />
        <div className={`comparison-view-header-right ${temporalContext?.anchorPosition === 'right' ? 'is-anchor' : ''} ${!effectiveRightRecord ? 'is-empty' : ''}`}>
          {/* Only show color bar when comparison record exists */}
          {effectiveRightRecord && effectiveRightColor && (
            <span
              className="comparison-view-color-bar"
              style={{ backgroundColor: effectiveRightColor }}
            />
          )}
          <Text strong type="secondary" style={{ fontSize: 12, textTransform: 'uppercase' }}>
            {temporalContext?.anchorPosition === 'right' && (
              <PushpinOutlined className="comparison-view-anchor-pin" />
            )}
            {labels.right}
          </Text>
        </div>
      </div>

      {/* Toolbar - only shown when there are controls to display */}
      {hasToolbarControls && (
        <div className="comparison-view-toolbar">
          <Space>
            <DiffOutlined
              style={{
                color: hasBothRecords && hasDifferences
                  ? 'var(--rp-gray-700)'
                  : hasBothRecords
                    ? 'var(--rp-success)'
                    : 'var(--rp-gray-500)'
              }}
            />
            {!hasBothRecords ? (
              <Text type="secondary">
                {totalFieldCount} field{totalFieldCount !== 1 ? 's' : ''} to compare
              </Text>
            ) : hasDifferences ? (
              <Text>
                <Text strong style={{ color: 'var(--rp-gray-700)' }}>{changedCount}</Text>
                {' '}field{changedCount !== 1 ? 's' : ''} changed
                {unchangedCount > 0 && !showDifferencesOnly && (
                  <Text type="secondary">
                    , {unchangedCount} unchanged
                  </Text>
                )}
              </Text>
            ) : (
              <Text type="success">
                No differences found
              </Text>
            )}
          </Space>

          <Space size="middle">
            {/* Swap button (arbitrary mode only) */}
            {showSwapButton && mode === 'arbitrary' && onSwap && (
              <Tooltip title="Swap records">
                <Button
                  type="text"
                  size="small"
                  icon={<SwapOutlined />}
                  onClick={onSwap}
                  aria-label="Swap comparison records"
                />
              </Tooltip>
            )}

            {/* Differences only toggle */}
            {onToggleShowDifferencesOnly && (
              <Space>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Differences only
                </Text>
                <Switch
                  size="small"
                  checked={showDifferencesOnly}
                  onChange={onToggleShowDifferencesOnly}
                  disabled={!hasBothRecords}
                />
              </Space>
            )}

            {/* Copy button */}
            {showCopyButton && (
              <Tooltip title="Copy comparison to clipboard">
                <Button
                  type="text"
                  size="small"
                  icon={isCopied ? <CheckOutlined style={{ color: 'var(--rp-success)' }} /> : <CopyOutlined />}
                  onClick={handleCopy}
                  aria-label="Copy comparison to clipboard"
                  disabled={!hasBothRecords}
                />
              </Tooltip>
            )}
          </Space>
        </div>
      )}

      {/* Content area wrapper - positions overlay relative to viewport, not scrollable content */}
      <div className={`comparison-view-content-wrapper ${hasLeftRecord && !hasRightRecord ? 'has-overlay' : ''}`}>
        {/* Educational overlay when no comparison selected - covers only the right column */}
        {hasLeftRecord && !hasRightRecord && (
          <div className="comparison-view-overlay comparison-view-overlay--right">
            <div className="comparison-view-overlay-content">
              <Text type="secondary" style={{ fontSize: 13, textAlign: 'center' }}>
                Select a row from the timeline<br />to compare values
              </Text>
            </div>
          </div>
        )}

        {/* Scrollable content */}
        <div
          className="comparison-view-content"
          style={{ maxHeight: maxHeight }}
        >
          {sections.length === 0 ? (
          <div className="comparison-view-empty">
            <Empty
              description={
                showDifferencesOnly && hasBothRecords
                  ? 'No differences found'
                  : !hasLeftRecord && !hasRightRecord
                    ? 'No record to display'
                    : 'No comparable fields available'
              }
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </div>
        ) : collapsible ? (
          <div className="comparison-view-sections">
            {sections.map(section => (
              <ComparisonSection
                key={section.key}
                section={section}
                fields={fields}
                keyFields={keyFields}
                expanded={!collapsedSections.has(section.key)}
                onExpandChange={(expanded) => handleSectionToggle(section.key, expanded)}
                mode={mode}
                hasComparison={hasBothRecords}
                showDifferencesOnly={showDifferencesOnly}
                temporalContext={temporalContext}
                leftColumnColor={effectiveLeftColor}
                rightColumnColor={effectiveRightColor}
              />
            ))}
          </div>
        ) : (
          /* Flat mode: render all rows without section wrappers */
          <div className="comparison-view-flat" role="list">
            {sections.flatMap(section =>
              section.fields.map(diff => (
                <ComparisonRow
                  key={diff.fieldKey}
                  difference={diff}
                  metadata={fields[diff.fieldKey]}
                  isKeyField={keyFields.has(diff.fieldKey)}
                  mode={mode}
                  hasComparison={hasBothRecords}
                  temporalContext={temporalContext}
                  leftColumnColor={effectiveLeftColor}
                  rightColumnColor={effectiveRightColor}
                />
              ))
            )}
          </div>
        )}
        </div>
      </div>
    </div>
  )
}

export default ComparisonView
